/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { HttpParams } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { concatMap, first, map } from 'rxjs/operators';
import { MyInjector } from '@src/app/app.module';
import { AbstractObject } from '@src/app/model/abstract-object';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { RequestService } from '@src/app/modules/crm/request.service';
import type { NewRequestData } from '@src/app/modules/crm/request-context.service';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { PickDialogComponent } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { Field } from '@src/app/shared/field/Field';
import { GridField } from '@src/app/shared/grid/grid-field';
import { GridControlParameters } from '@src/app/shared/grid/grid-control-parameters';
import { GridControl } from '@src/app/shared/grid/grid-control';
import { AppFormControl } from '@src/app/shared/form/app-form-control';
import { NavRoute } from '@src/app/shared/NavRoute';
import { RelatedRequestsComponent } from '@src/app/shared/related-requests/related-requests.component';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { FormPillComponent, PillField } from '@src/app/shared/form/form-pill/form-pill.component';
import { makeActionStatusPillField, makeStatusPillField } from '@src/app/modules/crm/service-request-page/requestPillFields';
import { makeRequestChannelPillField } from '@src/app/modules/social/channelPillFields';

/** Who started the link - the user pressing the button, or the page offering unasked. */
export type LinkTrigger = 'userClickedAdd' | 'offeredAutoMagically';

/**
 * A list of service requests linked to another record (the child requests of a request, the
 * requests raised against a unit, ...). Full <app-grid> by default; call showSimpleVersion() for
 * the stripped-back filter-chips list used on the reworked request page.
 */
export class RelatedRequestsField extends GridField {
    private requestSvc = MyInjector.instance.get(RequestService);
    private dialog = MyInjector.instance.get(MatDialog);
    private cds = MyInjector.instance.get(ConfirmDialogService);

    showSimple = false;
    allowSimpleAdd = true;
    /** The linked requests, for the simple view's plain <table> (grid rows are built separately). */
    srList: ServiceRequest[] = [];

    /** Detail route for the ref link and the "expand" dialog's full grid. */
    navRoute: NavRoute;
    /**
     * The request a child is raised against. The stepper needs the whole parent (team, wording,
     * files), not just an id, so a list that hangs off a unit or contact leaves this null and
     * offers no add buttons at all.
     */
    parentRequest: () => ServiceRequest = () => null;
    /**
     * For a host that is not a request but can still raise one - a ballot names its team, and what
     * the new request is linked to. Only consulted when there is no parent request.
     */
    newRequestData: () => NewRequestData = null;
    /** What the host is called in the add buttons' tips - "Add a new case linked to this ballot". */
    newRequestNoun = $localize`record`;
    /** Called after a request is added or changed, so the host can reload. created is the new one, if any. */
    changed: (created?: ServiceRequest) => void = () => undefined;

    linkRequest: (sr: ServiceRequest) => Observable<unknown> = null;
    linkFilter: () => Record<string, string> = () => ({});
    linkLabel = $localize`Link Request`;
    linkPrompt = '';
    unlinkRequest: (sr: ServiceRequest) => Observable<unknown> = null;

    /*
     * Channel / Status / Action Status pills. The simple <table> reads these instances directly (stateless
     * getFormattedValue / styleFor / colorFor / tipFor / gridClick); the full grid manufactures its own
     * per-row pill fields in the rowFactory below.
     */
    channelPillField: PillField = makeRequestChannelPillField();
    statusPillField: PillField = makeStatusPillField();
    actionPillField: PillField = makeActionStatusPillField();

    constructor(options: Partial<RelatedRequestsField> = {}) {
        const field = new Field({
            name: options.value ?? 'childRequests',
            label: options.label ?? 'Requests',
            value: options.value ?? 'childRequests',
            type: 'grid',
            sendServer: false,
            visible: Field.formOnly,
        });
        super({ field, rowFactory: () => [] } as GridControlParameters);
        Object.assign(this, options);
    }

    showSimpleVersion({ adds = true }: { adds?: boolean } = {}) {
        this.showSimple = true;
        this.allowSimpleAdd = adds;
        return this;
    }

    makeControl(): AppFormControl | GridControl {
        this.config.rowFactory = () => [
            FormNavLinkComponent.makeDetail('Ref', 'refNr', 'id', this.navRoute).override({
                readonly: true, sendServer: false, cellOpts: { width: '5em', maxWidth: '5em' },
            }),
            FormTextComponent.make('Title', 'title', { readonly: true }),
            FormTextAreaComponent.make('Description', 'description', { readonly: true }),
            makeRequestChannelPillField(),
            makeStatusPillField(),
            makeActionStatusPillField(),
            ServiceRequest.getOwnedByROField(),
            ServiceRequest.getCreatedField(),
            ServiceRequest.getLastUpdatedField(),
        ];
        this.control = new GridControl(this);
        // GridControl's constructor forces formControlFactory to GridComponent - put ours back so
        // the simple view (header + filter chips + expand) renders, not the raw grid.
        this.formControlFactory = RelatedRequestsComponent.createComponent;
        return this.control;
    }

    setValue(item: AbstractObject, readonly: boolean) {
        // super reads the array at this.value and builds the grid rows; grab it too for the simple view.
        this.srList = (this.getValue(item) as ServiceRequest[]) ?? [];
        super.setValue(item, readonly);
    }

    link(trigger: LinkTrigger) {
        this.candidates().pipe(concatMap(list => this.pick(list, trigger))).subscribe(picked => {
            if (picked) {
                this.linkRequest(picked).subscribe(linked => {
                    if (linked) { this.addRequest(picked); }
                });
            }
        });
    }

    /** What the filter allows, less what is already listed - relinking one of those means nothing. */
    private candidates(): Observable<ServiceRequest[]> {
        const filter = this.linkFilter();
        let parms = new HttpParams();
        // An empty one is left out rather than sent as "undefined", which would match nothing.
        Object.keys(filter).forEach(key => {
            if (filter[key]) { parms = parms.set(key, filter[key]); }
        });
        const listed = new Set(this.srList.map(sr => sr.id));

        return this.requestSvc.get<ServiceRequest>(false, parms).pipe(first(),
            map(list => list.filter(sr => !listed.has(sr.id))));
    }

    /*
     * Fetched before the dialog rather than left to its own service call, so the ones already listed
     * can be taken out before it is shown.
     */
    private pick(candidates: ServiceRequest[], trigger: LinkTrigger): Observable<ServiceRequest | null> {
        if (candidates.length === 0) {
            if (trigger === 'userClickedAdd') {
                this.cds.alert(this.linkLabel, $localize`There is nothing left to link.`);
            }
            return of(null);
        }
        return this.dialog.open(PickDialogComponent, {
            // width is a percentage of the screen - three narrow columns do not need the default 80.
            data: { config: this.pickConfig(), service: this.requestSvc, items: candidates, showSearch: true,
                width: 40, prompt: trigger === 'offeredAutoMagically' ? this.linkPrompt : '' }
        }).afterClosed().pipe(first());
    }

    private pickConfig(): FormConfig {
        return new FormConfig({
            title: this.label,
            mode: 'list',
            fieldSet: new FieldSet({
                fields: [
                    // A real link, so a request can be opened in its own tab and checked first.
                    FormNavLinkComponent.makeDetail($localize`Ref`, 'refNr', 'id', this.navRoute),
                    FormTextComponent.make('Title', 'title'),
                    // The same pill the request wears everywhere else, so what state each candidate
                    // is in is read at a glance. No click flow on this one: clicking a pill here
                    // picks the row, which is all the dialog is for.
                    FormPillComponent.make($localize`Status`, 'srStatus', { states: ServiceRequest.srStates }),
                ],
                formLayout: LAYOUT_OPTIONS.singleCol,
            }),
        });
    }

    /** Both views, because the grid behind "Expand" is built separately from the simple table. */
    addRequest(sr: ServiceRequest) {
        this.srList.push(sr);
        this.control?.addRow(sr, true, true);
    }

    /**
     * Everything off the list. For a host whose own change invalidates the lot - a purchase moving
     * to another supplier, whose work orders these are not.
     */
    clearRequests() {
        this.srList.length = 0;
        if (this.control) {
            this.deleteAll();
        }
    }

    removeRequest(sr: ServiceRequest) {
        const index = this.srList.indexOf(sr);
        if (index >= 0) {
            this.srList.splice(index, 1);
        }
        // Not GridRow.delete() - that only flags a row with an id, and this one is already gone.
        const row = this.control?.gridRows().find(r => r.focus === sr);
        if (row) {
            this.control.removeGridRow(row);
            this.control.refresh();
        }
    }
}
